import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import StockNavBar from "../components/StockNavBar";
import { Plus, Trash2, Edit, Package, AlertTriangle, ArrowUp, ArrowDown, Download, Copy, Search, X, ArrowUpDown, ShoppingCart, ChevronUp, ChevronDown, ArrowRightLeft, ExternalLink, Clock, User, Filter, TrendingUp, TrendingDown, LineChart as LineChartIcon, Award, Truck, ChefHat, Archive, Flame } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SecoesUnidade, Secao, SecaoEstoque, SecaoPerda, ResumoUnidade, paraPayload, minMaxValido } from "../features/estoque-unidade";
import { TabelaItensEstoque } from "@/features/estoque";
import { ProducaoTab } from "@/features/producao";
import { Switch } from "../components/ui/switch";
import { exportToExcel } from "../lib/utils";
import { temPermissao } from "../lib/permissoes";
import TablePagination from "../components/TablePagination";
import { CarregandoBloco, CarregandoCards, CarregandoTabela } from "../components/carregando";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// URL relativa - funciona em qualquer domínio
const API = '/api';

/** Quantidade com vírgula (12,50), como o resto do painel. */
const fmtNum = (v, casas = 2) => (Number(v) || 0).toFixed(casas).replace(".", ",");

/** Estoque em unidades e inteiro: "2,5 un" nao existe. */
const arredondarInteiro = (x) => (x === "" || x == null ? "" : String(Math.round(Number(x) || 0)));

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Busca leve de ingredientes pra adicionar na receita — mesmo padrao do
// IngredientCombobox de Products.js, sem o overkill de portal positioning.
function RecipeIngredientSearch({ ingredients, excludeIds, onAdd }) {
  const [search, setSearch] = useState("");
  const [openList, setOpenList] = useState(false);
  const excludeSet = useMemo(() => new Set((excludeIds || []).filter(Boolean)), [excludeIds]);
  const filtered = useMemo(() => {
    const term = (search || "").toLowerCase().trim();
    return ingredients
      .filter(i => !excludeSet.has(i.id))
      .filter(i => !term || i.name.toLowerCase().includes(term) || (i.code && String(i.code).includes(term)))
      .slice(0, 30);
  }, [ingredients, excludeSet, search]);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          placeholder="Buscar ingrediente para adicionar..."
          className="pl-9 h-10"
        />
      </div>
      {openList && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.map(i => (
            <button
              key={i.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onAdd(i.id); setSearch(""); setOpenList(false); }}
              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center gap-2"
            >
              {i.is_recipe && <ChefHat className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
              <span className="flex-1 truncate">{i.name}</span>
              {i.code && <span className="text-[10px] text-muted-foreground font-mono">{i.code}</span>}
              <span className="text-xs text-muted-foreground">{i.unit}</span>
            </button>
          ))}
        </div>
      )}
      {openList && filtered.length === 0 && (search || "").trim() && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border rounded-md shadow-lg p-3 text-xs text-muted-foreground">
          Nenhum item encontrado pra "{search}".
        </div>
      )}
    </div>
  );
}

export default function Ingredients() {
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentIngredientId, setCurrentIngredientId] = useState(null);
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [unitsPerPackage, setUnitsPerPackage] = useState("");
  const [unitWeight, setUnitWeight] = useState("");


  // % de perda apos preparo (higienizacao, aparo, etc) — afeta o CMV efetivo.
  const [lossPercent, setLossPercent] = useState("");
  const [stockMin, setStockMin] = useState("");
  const [stockMax, setStockMax] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // ===== RECEITAS (cadastro em Estoque) =====
  // Receita = item composto que vira ingrediente (linked_ingredient_id), pra ser usado em outros produtos.
  // Salva: 1) cria/atualiza ingrediente com is_recipe=true; 2) cria/atualiza product type=receita com recipe_yield + ingredients.
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [recipeEditMode, setRecipeEditMode] = useState(false);
  const [recipeIngredientId, setRecipeIngredientId] = useState(null); // ID do ingrediente vinculado (Estoque)
  const [recipeProductId, setRecipeProductId] = useState(null);       // ID do product type=receita
  const [recipeName, setRecipeName] = useState("");
  const [recipeYieldQty, setRecipeYieldQty] = useState("");
  const [recipeYieldUnit, setRecipeYieldUnit] = useState("kg");
  const [recipeLossPercent, setRecipeLossPercent] = useState(""); // % de perda apos preparo
  const [recipeItems, setRecipeItems] = useState([]); // [{ ingredient_id, quantity }]
  const [recipeSaving, setRecipeSaving] = useState(false);
  
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStock, setFilterStock] = useState("all"); // all, low, normal, high
  
  // Ordenação
  const [sortField, setSortField] = useState(null); // average_price, stock_quantity, stock_min, stock_max
  const [sortDirection, setSortDirection] = useState("asc"); // asc, desc
  
  // Stock adjustment dialog
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockIngredient, setStockIngredient] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState("");
  const [stockOperation, setStockOperation] = useState("add");

  // Sub-aba ativa
  const [activeTab, setActiveTab] = useState("estoque"); // "estoque" ou "movimentacoes"

  // Modal de análise de preços de um ingrediente
  const [priceAnalysisOpen, setPriceAnalysisOpen] = useState(false);
  const [priceAnalysisData, setPriceAnalysisData] = useState(null);
  const [priceAnalysisLoading, setPriceAnalysisLoading] = useState(false);
  // Filtro de data do gráfico: 'all' | 'month' | 'custom'
  const [priceDateFilter, setPriceDateFilter] = useState('all');
  const [priceCustomStart, setPriceCustomStart] = useState('');
  const [priceCustomEnd, setPriceCustomEnd] = useState('');
  const openPriceAnalysis = async (ingredient) => {
    setPriceAnalysisOpen(true);
    setPriceAnalysisLoading(true);
    setPriceDateFilter('all');
    setPriceCustomStart('');
    setPriceCustomEnd('');
    setPriceAnalysisData({ ingredient: { id: ingredient.id, name: ingredient.name, unit: ingredient.unit, is_recipe: ingredient.is_recipe || recipeIngredientIds.has(ingredient.id) }, history: [], stats: null });
    try {
      const res = await axios.get(`${API}/reports/price-analysis/${ingredient.id}`, getAuthHeader());
      setPriceAnalysisData(res.data);
    } catch (error) {
      toast.error("Erro ao carregar histórico de preços");
    } finally {
      setPriceAnalysisLoading(false);
    }
  };

  // Histórico filtrado pelo período selecionado
  const filteredPriceHistory = useMemo(() => {
    if (!priceAnalysisData?.history) return [];
    const all = priceAnalysisData.history;
    if (priceDateFilter === 'all') return all;
    if (priceDateFilter === 'month') {
      const now = new Date();
      const yyyymm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      return all.filter(h => String(h.date).startsWith(yyyymm));
    }
    if (priceDateFilter === 'custom') {
      if (!priceCustomStart && !priceCustomEnd) return all;
      return all.filter(h => {
        const d = String(h.date).split('T')[0];
        if (priceCustomStart && d < priceCustomStart) return false;
        if (priceCustomEnd && d > priceCustomEnd) return false;
        return true;
      });
    }
    return all;
  }, [priceAnalysisData, priceDateFilter, priceCustomStart, priceCustomEnd]);

  // Movimentações
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementFilter, setMovementFilter] = useState("all"); // all, purchase, order_deduction, order_restore, adjustment_in, adjustment_out
  const [movementSearch, setMovementSearch] = useState("");
  const [stockReason, setStockReason] = useState("");
  
  // Delete warning dialog (when ingredient is in use)
  const [deleteWarningOpen, setDeleteWarningOpen] = useState(false);
  const [productsUsingIngredient, setProductsUsingIngredient] = useState([]);
  
  // Delete confirmation dialog (normal delete)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null); // {id, name, compras} — item com compras vai pra Arquivados
  const [ingredientToDelete, setIngredientToDelete] = useState(null);
  
  // Duplicate dialog
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [ingredientToDuplicate, setIngredientToDuplicate] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estados para edição inline de quantidade mínima/máxima
  const [editingMin, setEditingMin] = useState(null);
  const [editingMax, setEditingMax] = useState(null);
  const [editMinValue, setEditMinValue] = useState("");
  const [editMaxValue, setEditMaxValue] = useState("");
  
  // Valor total em estoque
  const [stockValue, setStockValue] = useState({ total_value: 0, items_with_stock: 0, total_items: 0 });
  // Carregamento com cards (25/10/2026): cards do topo e tabela piscam até a
  // 1ª busca voltar — antes abria com R$ 0,00 e "Nenhum item cadastrado".
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [movimentosCarregados, setMovimentosCarregados] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([fetchIngredients(), fetchStockValue()]).finally(() => setPrimeiraCarga(false));
    fetchRecipeIngredientIds();
    loadCurrentUser();
    ensureRecipeCategory();
  }, []);

  useEffect(() => {
    if (activeTab === "movimentacoes" && movements.length === 0) {
      fetchMovements();
    }
  }, [activeTab]);

  const loadCurrentUser = () => {
    const user = localStorage.getItem("user");
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
  };

  // Garantir que a categoria "Receita" existe
  const ensureRecipeCategory = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeader());
      const existingCategories = response.data;
      const recipeCategory = existingCategories.find(c => c.name === "Receita");
      if (!recipeCategory) {
        await axios.post(`${API}/categories`, { name: "Receita" }, getAuthHeader());
      }
    } catch (error) {
      console.error("Erro ao verificar categoria Receita:", error);
    }
  };

  // Set de ingredient_ids que sao receitas (vinculados a algum product type=receita)
  // Usado pra estilizar rows mesmo se o flag is_recipe nao tiver sido setado
  // (caso de receitas antigas criadas antes da feature 'Nova Receita').
  const [recipeIngredientIds, setRecipeIngredientIds] = useState(() => new Set());

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`, getAuthHeader());
      setIngredients(response.data);
    } catch (error) {
      toast.error("Erro ao carregar ingredientes");
    }
  };

  const fetchRecipeIngredientIds = async () => {
    try {
      const [pRes, iRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeader()),
        // Re-fetch ingredientes pra ter o map de id->name aqui mesmo (sem racing com setIngredients)
        axios.get(`${API}/ingredients`, getAuthHeader()).catch(() => ({ data: ingredients })),
      ]);
      const products = pRes.data || [];
      const ings = iRes.data || ingredients;
      const ids = new Set();
      // 1) Match direto por linked_ingredient_id
      for (const p of products) {
        if (p?.product_type === "receita" && p?.linked_ingredient_id) {
          ids.add(p.linked_ingredient_id);
        }
      }
      // 2) Fallback: match por NOME (caso linked_ingredient_id tenha sido perdido)
      const ingredientsByName = new Map();
      for (const i of ings) {
        const k = (i.name || "").trim().toLowerCase();
        if (k) ingredientsByName.set(k, i.id);
      }
      for (const p of products) {
        if (p?.product_type !== "receita") continue;
        if (p?.linked_ingredient_id) continue; // ja resolvido pelo match direto
        const matchId = ingredientsByName.get((p.name || "").trim().toLowerCase());
        if (matchId) ids.add(matchId);
      }
      setRecipeIngredientIds(ids);
    } catch (error) {
      // silencioso — UI continua funcionando, so perde o highlight pras receitas antigas
    }
  };

  const fetchStockValue = async () => {
    try {
      const response = await axios.get(`${API}/ingredients/stats/stock-value`, getAuthHeader());
      setStockValue(response.data);
    } catch (error) {
      console.error("Erro ao carregar valor do estoque");
    }
  };
  
  const fetchMovements = async () => {
    setMovementsLoading(true);
    try {
      const response = await axios.get(`${API}/stock-movements?limit=500`, getAuthHeader());
      setMovements(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar movimentações");
    } finally {
      setMovementsLoading(false);
      setMovimentosCarregados(true);
    }
  };

  // Função para iniciar edição de quantidade mínima
  const startEditMin = (ingredient) => {
    setEditingMin(ingredient.id);
    setEditMinValue(ingredient.stock_min ? ingredient.stock_min.toString() : "");
  };

  // Função para iniciar edição de quantidade máxima
  const startEditMax = (ingredient) => {
    setEditingMax(ingredient.id);
    setEditMaxValue(ingredient.stock_max ? ingredient.stock_max.toString() : "");
  };

        // Função para salvar quantidade mínima
  const saveMinValue = async (ingredientId) => {
    try {
      const value = editMinValue ? parseFloat(editMinValue) : 0;
      const ingredient = ingredients.find(i => i.id === ingredientId);
      if (!ingredient) return;

      await axios.put(
        `${API}/ingredients/${ingredientId}`,
        {
          name: ingredient.name,
          unit: ingredient.unit,
          category: null,
          stock_min: value,
          stock_max: ingredient.stock_max || 0,
          is_recipe: ingredient.is_recipe || false
        },
        getAuthHeader()
      );
      toast.success("Quantidade mínima atualizada!");
      setEditingMin(null);
      setEditMinValue("");
      fetchIngredients();
    } catch (error) {
      toast.error("Erro ao atualizar quantidade mínima");
    }
  };

  // Função para salvar quantidade máxima
  const saveMaxValue = async (ingredientId) => {
    try {
      const value = editMaxValue ? parseFloat(editMaxValue) : 0;
      const ingredient = ingredients.find(i => i.id === ingredientId);
      if (!ingredient) return;

      await axios.put(
        `${API}/ingredients/${ingredientId}`,
        {
          name: ingredient.name,
          unit: ingredient.unit,
          category: null,
          stock_min: ingredient.stock_min || 0,
          stock_max: value,
          is_recipe: ingredient.is_recipe || false
        },
        getAuthHeader()
      );
      toast.success("Quantidade máxima atualizada!");
      setEditingMax(null);
      setEditMaxValue("");
      fetchIngredients();
    } catch (error) {
      toast.error("Erro ao atualizar quantidade máxima");
    }
  };

  // Filtrar ingredientes
  const filteredIngredients = useMemo(() => {
    return ingredients.filter(ingredient => {
      if (ingredient.is_active === false) return false; // arquivado: aba propria
      // Filtro por texto (nome ou código)
      const matchesSearch = searchTerm === "" || 
        ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ingredient.code && ingredient.code.includes(searchTerm));
      
      // Filtro por status de estoque
      let matchesStock = true;
      if (filterStock !== "all") {
        const qty = ingredient.stock_quantity || 0;
        const min = ingredient.stock_min || 0;
        const max = ingredient.stock_max || 0;
        
        if (filterStock === "low") {
          matchesStock = min > 0 && qty <= min;
        } else if (filterStock === "high") {
          matchesStock = max > 0 && qty >= max;
        } else if (filterStock === "normal") {
          matchesStock = (min === 0 || qty > min) && (max === 0 || qty < max);
        }
      }
      
      return matchesSearch && matchesStock;
    });
  }, [ingredients, searchTerm, filterStock]);
  
  // Ingredientes ordenados
  const sortedIngredients = useMemo(() => {
    if (!sortField) return filteredIngredients;
    
    return [...filteredIngredients].sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      
      if (sortDirection === "asc") {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
  }, [filteredIngredients, sortField, sortDirection]);

  // Ingredientes paginados
  const paginatedIngredients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedIngredients.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedIngredients, currentPage, itemsPerPage]);
  
  // Função para alternar ordenação
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStock]);
  
  // Calcular valor necessário para completar estoque mínimo
  const stockDeficitValue = useMemo(() => {
    let totalDeficit = 0;
    let itemsWithDeficit = 0;
    
    ingredients.forEach(ing => {
      if (ing.is_active === false) return;
      const qty = ing.stock_quantity || 0;
      const min = ing.stock_min || 0;
      const price = ing.average_price || 0;
      const unitsPerPackage = ing.units_per_package || 0;
      const ingredientUnit = (ing.unit || "").toLowerCase();
      
      if (min > 0 && qty < min) {
        // price (average_price) é sempre por UNIDADE
        // qty e min estão em unidades
        const deficit = min - qty;
        totalDeficit += deficit * price;
        itemsWithDeficit++;
      }
    });
    
    return { value: totalDeficit, items: itemsWithDeficit };
  }, [ingredients]);

  const resetForm = () => {
    setName("");
    setUnit("");
    setUnitsPerPackage("");
    setUnitWeight("");
    setLossPercent("");
    setStockMin("");
    setStockMax("");
    setEditMode(false);
    setCurrentIngredientId(null);
  };

  // ===== Helpers de receita =====
  const resetRecipeForm = () => {
    setRecipeEditMode(false);
    setRecipeIngredientId(null);
    setRecipeProductId(null);
    setRecipeName("");
    setRecipeYieldQty("");
    setRecipeYieldUnit("kg");
    setRecipeLossPercent("");
    setRecipeItems([]);
  };

  // Calcula custo de 1 item da receita usando a mesma logica de Products.calculateRecipeCosts
  const calcRecipeItemCost = (ing, qty) => {
    if (!ing) return 0;
    const avg = Number(ing.average_price) || 0;
    const u = ing.unit || "";
    const uw = Number(ing.unit_weight) || 0;
    const loss = Number(ing.loss_percent) || 0;
    const lossF = (loss > 0 && loss < 100) ? 1 / (1 - loss / 100) : 1;
    const q = Number(qty) || 0;
    let base;
    if (u === "kg" && uw > 0) base = avg * uw * q;
    else if (u === "un" && uw > 0) base = (avg / uw) * q;
    else base = avg * q;
    return base * lossF;
  };

  const recipeTotalCost = recipeItems.reduce((sum, it) => {
    const ing = ingredients.find(i => i.id === it.ingredient_id);
    return sum + calcRecipeItemCost(ing, it.quantity);
  }, 0);

  const recipeUnitCost = (() => {
    const yq = parseFloat(recipeYieldQty);
    if (!Number.isFinite(yq) || yq <= 0) return 0;
    return recipeTotalCost / yq;
  })();

  const addRecipeItem = () => setRecipeItems(prev => [...prev, { ingredient_id: "", quantity: "" }]);
  const removeRecipeItem = (idx) => setRecipeItems(prev => prev.filter((_, i) => i !== idx));
  const updateRecipeItem = (idx, field, value) => {
    setRecipeItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  // Abre o modal de receita pra editar uma existente — busca o product linkado pelo ingredient_id
  const handleEditRecipe = async (ingredient) => {
    setRecipeOpen(true);
    setRecipeEditMode(true);
    setRecipeIngredientId(ingredient.id);
    setRecipeName(ingredient.name);
    setRecipeYieldUnit(ingredient.unit || "kg");
    setRecipeLossPercent(ingredient.loss_percent && ingredient.loss_percent > 0 ? String(ingredient.loss_percent) : "");
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      const all = res.data || [];
      // 1) Match direto por linked_ingredient_id
      let prod = all.find(p => p.linked_ingredient_id === ingredient.id && p.product_type === "receita");
      // 2) Fallback: mesmo nome (case-insensitive) e product_type=receita, sem link salvo
      if (!prod) {
        const targetName = (ingredient.name || "").trim().toLowerCase();
        prod = all.find(p => p.product_type === "receita"
          && !p.linked_ingredient_id
          && (p.name || "").trim().toLowerCase() === targetName);
      }
      if (!prod) {
        toast.warning("Produto da receita não encontrado, criando vínculo novo.");
        setRecipeProductId(null);
        setRecipeYieldQty("");
        setRecipeItems([{ ingredient_id: "", quantity: "" }]);
        return;
      }
      setRecipeProductId(prod.id);
      setRecipeYieldQty(prod.recipe_yield != null ? String(prod.recipe_yield) : "");
      const items = Array.isArray(prod.recipe) ? prod.recipe : [];
      setRecipeItems(items.length > 0
        ? items.map(r => ({ ingredient_id: r.ingredient_id, quantity: r.quantity != null ? String(r.quantity) : "" }))
        : [{ ingredient_id: "", quantity: "" }]
      );
      // Auto-relink: se chegou via fallback de nome, persiste o vinculo no banco
      // pra a proxima edicao ja achar via match direto.
      if (!prod.linked_ingredient_id) {
        try {
          await axios.put(`${API}/products/${prod.id}`, { ...prod, linked_ingredient_id: ingredient.id }, getAuthHeader());
        } catch {}
      }
    } catch (error) {
      toast.error("Erro ao carregar receita.");
    }
  };

  // Receita sem o item pronto no estoque (sem linked_ingredient_id e sem item
  // de mesmo nome): o lápis da aba Produção não fazia nada. Abre a receita pelo
  // produto; ao salvar, o fluxo normal cria o item pronto e refaz o vínculo.
  const editarReceitaPorProduto = async (productId) => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      const prod = (res.data || []).find(p => p.id === productId);
      if (!prod) { toast.error("Receita não encontrada."); return; }
      resetRecipeForm();
      setRecipeEditMode(true);
      setRecipeProductId(prod.id);
      setRecipeName(prod.name || "");
      setRecipeYieldUnit(prod.recipe_yield_unit || "kg");
      setRecipeYieldQty(prod.recipe_yield != null && prod.recipe_yield > 0 ? String(prod.recipe_yield) : "");
      const items = Array.isArray(prod.recipe) ? prod.recipe : [];
      setRecipeItems(items.length > 0
        ? items.map(r => ({ ingredient_id: r.ingredient_id, quantity: r.quantity != null ? String(r.quantity) : "" }))
        : [{ ingredient_id: "", quantity: "" }]
      );
      setRecipeOpen(true);
    } catch (error) {
      toast.error("Erro ao carregar receita.");
    }
  };

  const openNewRecipe = () => {
    resetRecipeForm();
    setRecipeItems([{ ingredient_id: "", quantity: "" }]);
    setRecipeOpen(true);
  };

  // Cria/atualiza receita: 1 ingrediente vinculado (is_recipe=true) + 1 produto type=receita
  const submitRecipe = async (e) => {
    e?.preventDefault?.();
    if (!recipeName.trim()) { toast.error("Nome da receita é obrigatório."); return; }
    const yq = parseFloat(recipeYieldQty);
    if (!Number.isFinite(yq) || yq <= 0) { toast.error("Informe o rendimento da receita."); return; }
    const validItems = recipeItems.filter(it => it.ingredient_id && parseFloat(it.quantity) > 0);
    if (validItems.length === 0) { toast.error("Adicione pelo menos 1 ingrediente com quantidade."); return; }

    setRecipeSaving(true);
    try {
      let ingredientId = recipeIngredientId;
      // 1) Cria ou atualiza o ingrediente vinculado (com is_recipe=true e unit=yieldUnit)
      const _rloss = parseFloat(recipeLossPercent);
      const ingPayload = {
        name: recipeName.trim(),
        unit: recipeYieldUnit,
        category: null,
        stock_min: 0,
        stock_max: 0,
        is_recipe: true,
        loss_percent: (Number.isFinite(_rloss) && _rloss > 0 && _rloss < 100) ? _rloss : 0,
      };
      if (recipeEditMode && ingredientId) {
        await axios.put(`${API}/ingredients/${ingredientId}`, ingPayload, getAuthHeader());
      } else {
        const res = await axios.post(`${API}/ingredients`, ingPayload, getAuthHeader());
        ingredientId = res.data.id;
      }

      // 2) Cria/atualiza o produto type=receita
      const productPayload = {
        name: recipeName.trim(),
        category: "Receita",
        product_type: "receita",
        // is_insumo=true: receita eh producao interna, nao aparece no cardapio publico
        is_insumo: true,
        is_divisible: false,
        recipe: validItems.map(it => {
          const ing = ingredients.find(i => i.id === it.ingredient_id);
          return {
            ingredient_id: it.ingredient_id,
            ingredient_name: ing?.name || "",
            quantity: parseFloat(it.quantity) || 0,
            item_type: "ingredient",
          };
        }),
        recipe_yield: yq,
        recipe_yield_unit: recipeYieldUnit,
        linked_ingredient_id: ingredientId,
      };
      if (recipeEditMode && recipeProductId) {
        await axios.put(`${API}/products/${recipeProductId}`, productPayload, getAuthHeader());
      } else {
        await axios.post(`${API}/products`, productPayload, getAuthHeader());
      }

      toast.success(recipeEditMode ? "Receita atualizada!" : "Receita criada!");
      setRecipeOpen(false);
      resetRecipeForm();
      fetchIngredients();
      fetchRecipeIngredientIds();
    } catch (error) {
      console.error("Erro receita:", error);
      toast.error(error?.response?.data?.detail || "Erro ao salvar receita");
    } finally {
      setRecipeSaving(false);
    }
  };

  const handleEdit = (ingredient) => {
    setEditMode(true);
    setCurrentIngredientId(ingredient.id);
    setName(ingredient.name);
    setUnit(ingredient.unit);
    setUnitsPerPackage(ingredient.units_per_package ? ingredient.units_per_package.toString() : "");
    const _uw = ingredient.unit_weight ? ingredient.unit_weight.toString() : "";
    setUnitWeight(_uw);
    setLossPercent(ingredient.loss_percent && ingredient.loss_percent > 0 ? ingredient.loss_percent.toString() : "");
    setStockMin(ingredient.stock_min ? ingredient.stock_min.toString() : "");
    setStockMax(ingredient.stock_max ? ingredient.stock_max.toString() : "");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!unit) { toast.error("Escolha como você compra este item (kg, unidade ou caixa)."); return; }
    if (!minMaxValido(stockMin, stockMax)) { toast.error("O estoque mínimo precisa ser menor que o máximo."); return; }
    setLoading(true);
    try {
      const payload = {
        name,
        unit: unit === "unidade" ? "un" : unit,
        category: null,
        stock_min: stockMin ? parseFloat(stockMin) : 0,
        stock_max: stockMax ? parseFloat(stockMax) : 0,
      };

      // unit / units_per_package / unit_weight vem das duas perguntas (features/estoque-unidade)
      Object.assign(payload, paraPayload({ unit, unitsPerPackage, unitWeight }));

      // Perda apos preparo (validada entre 0 e 99)
      const _loss = parseFloat(lossPercent);
      payload.loss_percent = (Number.isFinite(_loss) && _loss > 0 && _loss < 100) ? _loss : 0;

      if (editMode) {
        await axios.put(`${API}/ingredients/${currentIngredientId}`, payload, getAuthHeader());
        toast.success("Item atualizado!");
      } else {
        await axios.post(`${API}/ingredients`, payload, getAuthHeader());
        toast.success("Item criado!");
      }
      
      resetForm();
      setOpen(false);
      fetchIngredients();
    } catch (error) {
      console.error("Erro:", error.response?.data || error);
      toast.error(error.response?.data?.detail || (editMode ? "Erro ao atualizar item" : "Erro ao criar item"));
    } finally {
      setLoading(false);
    }
  };

  // Duplicar item
  const openDuplicateDialog = (ingredient) => {
    setIngredientToDuplicate(ingredient);
    setDuplicateName(`${ingredient.name} (Cópia)`);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateIngredient = async () => {
    if (!ingredientToDuplicate || !duplicateName.trim()) {
      toast.error("Nome do item é obrigatório");
      return;
    }

    // Verificar se já existe um item com o mesmo nome
    const exists = ingredients.some(
      i => i.name.toLowerCase() === duplicateName.trim().toLowerCase()
    );

    if (exists) {
      toast.error("Já existe um item com este nome");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: duplicateName.trim(),
        unit: ingredientToDuplicate.unit,
        category: null,
        units_per_package: ingredientToDuplicate.units_per_package || 0,
        unit_weight: ingredientToDuplicate.unit_weight || 0,
        stock_min: ingredientToDuplicate.stock_min || 0,
        stock_max: ingredientToDuplicate.stock_max || 0,
        // Não copiar estoque atual nem preço médio
      };

      await axios.post(`${API}/ingredients`, payload, getAuthHeader());
      toast.success(`Item "${duplicateName}" criado com sucesso!`);
      setDuplicateDialogOpen(false);
      setIngredientToDuplicate(null);
      setDuplicateName("");
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao duplicar item");
    } finally {
      setLoading(false);
    }
  };

  const checkUsageAndDelete = async (id, ingredientName) => {
    try {
      const response = await axios.get(`${API}/ingredients/${id}/usage`, getAuthHeader());
      
      if ((response.data.used_in_products || []).length > 0) {
        setProductsUsingIngredient(response.data.used_in_products);
        setIngredientToDelete(ingredientName);
        setDeleteWarningOpen(true);
      } else if (response.data.must_archive) {
        // Tem compras: excluir apagaria historico e resultados — vai pra Arquivados
        setArchiveTarget({ id, name: ingredientName, compras: response.data.purchases_count });
      } else {
        setIngredientToDelete({ id, name: ingredientName });
        setDeleteConfirmOpen(true);
      }
    } catch (error) {
      toast.error("Erro ao verificar uso do item");
    }
  };

  const confirmDelete = async () => {
    if (!ingredientToDelete) return;
    
    try {
      await axios.delete(`${API}/ingredients/${ingredientToDelete.id}`, getAuthHeader());
      toast.success("Item excluído!");
      setDeleteConfirmOpen(false);
      setIngredientToDelete(null);
      fetchIngredients();
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 409) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Erro ao excluir item");
      }
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      await axios.patch(`${API}/ingredients/${archiveTarget.id}/archive`, {}, getAuthHeader());
      toast.success(`${archiveTarget.name} foi para Arquivados.`);
      setArchiveTarget(null);
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao arquivar item");
    }
  };

  const reactivateIngredient = async (ingredient) => {
    try {
      await axios.patch(`${API}/ingredients/${ingredient.id}/unarchive`, {}, getAuthHeader());
      toast.success(`${ingredient.name} reativado.`);
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao reativar item");
    }
  };

  // Stock adjustment
  const openStockDialog = (ingredient) => {
    setStockIngredient(ingredient);
    setStockAdjustment("");
    setStockOperation("add");
    setStockReason("");
    setStockDialogOpen(true);
  };

  const handleStockAdjustment = async () => {
    if (!stockIngredient || !stockAdjustment) return;
    
    try {
      await axios.put(
        `${API}/ingredients/${stockIngredient.id}/stock`,
        {
          quantity: parseFloat(stockAdjustment),
          operation: stockOperation,
          reason: stockReason || null
        },
        getAuthHeader()
      );
      toast.success(`Estoque ${stockOperation === "add" ? "adicionado" : "removido"} com sucesso!`);
      setStockDialogOpen(false);
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao ajustar estoque");
    }
  };


  // Dono, administrador ou funcionário com Estoque — a mesma regra do servidor.
  const canEdit = temPermissao(currentUser, "estoque");

  const getStockStatus = (ingredient) => {
    const qty = ingredient.stock_quantity || 0;
    const min = ingredient.stock_min || 0;
    const max = ingredient.stock_max || 0;
    
    if (min > 0 && qty <= min) {
      return { color: "text-red-500", bg: "bg-red-50 dark:bg-red-900/20", icon: AlertTriangle, label: "Baixo" };
    }
    if (max > 0 && qty >= max) {
      return { color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-900/20", icon: Package, label: "Alto" };
    }
    return { color: "text-green-500", bg: "bg-green-50 dark:bg-green-900/20", icon: Package, label: "Normal" };
  };

  // Contadores para os filtros
  const stockCounts = useMemo(() => {
    const counts = { low: 0, normal: 0, high: 0 };
    ingredients.forEach(ing => {
      if (ing.is_active === false) return;
      const status = getStockStatus(ing);
      if (status.label === "Baixo") counts.low++;
      else if (status.label === "Alto") counts.high++;
      else counts.normal++;
    });
    return counts;
  }, [ingredients]);
  

  return (
    <div className="p-8" data-testid="ingredients-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Estoque</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie os itens de estoque
          </p>
        </div>
        
        {/* Cards de Valor Total em Estoque */}
        {primeiraCarga ? (
          <CarregandoCards quantos={3} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" altura="h-[124px]" />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Valor Total em Estoque</p>
                <p className="text-3xl font-bold mt-1">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stockValue.total_value)}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6" />
              </div>
            </div>
            <p className="text-emerald-100 text-xs mt-2">
              {stockValue.items_with_stock} itens com estoque de {stockValue.total_items} ativos
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Necessário p/ Estoque Mín.</p>
                <p className="text-3xl font-bold mt-1">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stockDeficitValue.value)}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6" />
              </div>
            </div>
            <p className="text-blue-100 text-xs mt-2">
              {stockDeficitValue.items} itens abaixo do mínimo
            </p>
          </div>
          
          <div className={`bg-gradient-to-br ${stockCounts.low > 0 ? 'from-red-500 to-red-600' : 'from-zinc-500 to-zinc-600'} rounded-xl p-5 text-white shadow-lg cursor-pointer transition-transform hover:scale-[1.02]`} onClick={() => setFilterStock(stockCounts.low > 0 ? 'low' : 'all')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm font-medium">Estoque Baixo</p>
                <p className="text-3xl font-bold mt-1">{stockCounts.low}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>
            <p className="text-white/80 text-xs mt-2">
              {stockCounts.low > 0 ? 'Clique para filtrar itens críticos' : 'Tudo dentro do mínimo'}
            </p>
          </div>
          
        </div>
        )}

        <div className="w-full">

          {/* Sub-abas — centralizadas; no celular rolam de lado (a 1ª não some na borda) */}
          <div className="flex sm:justify-center mb-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit shrink-0">
            <button
              onClick={() => setActiveTab("estoque")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap shrink-0 transition-all ${
                activeTab === "estoque"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Package className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Estoque
            </button>
            <button
              onClick={() => setActiveTab("movimentacoes")}
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap shrink-0 transition-all ${
                activeTab === "movimentacoes"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ArrowRightLeft className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Movimentações
            </button>
            <button
              onClick={() => setActiveTab("arquivados")}
              data-e2e="tab-arquivados"
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap shrink-0 transition-all ${
                activeTab === "arquivados"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Archive className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Arquivados
              {ingredients.filter((i) => i.is_active === false).length > 0 && (
                <span className="ml-1.5 text-[10px] tabular-nums px-1.5 rounded-full bg-muted text-muted-foreground">{ingredients.filter((i) => i.is_active === false).length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("producao")}
              data-e2e="tab-producao"
              className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap shrink-0 transition-all ${
                activeTab === "producao"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Flame className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Produção
            </button>
          </div>
          </div>

          {/* Estoque */}
          {activeTab === "estoque" && (
          <div>
            {/* Ações: Exportar Excel + Novo Item — ACIMA da busca/tabela */}
            <div className="flex justify-end gap-2 mb-3">
              <Button
                variant="outline"
                onClick={() => exportToExcel(ingredients, "estoque", {
                  code: "Código",
                  name: "Nome",
                  unit: "Unidade",
                  average_price: "Preço Médio (últimas 5 compras)",
                  stock_quantity: "Qtd Estoque",
                  stock_min: "Estoque Mín",
                  stock_max: "Estoque Máx"
                })}
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
              {canEdit && (
                <Dialog open={open} onOpenChange={(isOpen) => {
                  setOpen(isOpen);
                  if (!isOpen) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-ingredient-button" className="shadow-sm">
                      <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                      Novo Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editMode ? "Editar Item" : "Novo Item de Estoque"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                      {/* Secoes numeradas na ordem de preenchimento (09/09/2026, pedido do dono) */}
                      <Secao numero={1} titulo="Item" ajuda="O código é gerado automaticamente." testId="secao-item">
                        <Input
                          id="name"
                          data-testid="ingredient-name-input"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Ex: Carne Bovina"
                          required
                          className="h-10"
                        />
                      </Secao>

                      <SecoesUnidade numero={2}
                        value={{ unit, unitsPerPackage, unitWeight }}
                        onChange={(v) => {
                          setUnit(v.unit); setUnitsPerPackage(v.unitsPerPackage); setUnitWeight(v.unitWeight);
                          // Unidade e caixa: estoque em numeros inteiros
                          if (v.unit === "un") { setStockMin(arredondarInteiro(stockMin)); setStockMax(arredondarInteiro(stockMax)); }
                        }}
                      />

                      <SecaoEstoque numero={4} unidadeEstoque={unit === "kg" ? "kg" : "un"}
                        stockMin={stockMin} setStockMin={setStockMin} stockMax={stockMax} setStockMax={setStockMax} />

                      <SecaoPerda numero={5} lossPercent={lossPercent} setLossPercent={setLossPercent} />

                      <ResumoUnidade value={{ unit, unitsPerPackage, unitWeight }} />

                      <Button
                        type="submit"
                        data-testid="create-ingredient-button"
                        disabled={loading || !unit}
                        className="w-full h-11 font-medium shadow-sm"
                      >
                        {loading ? (editMode ? "Atualizando..." : "Criando...") : (editMode ? "Atualizar Item" : "Criar Item")}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

            </div>

            {/* Card unificado: busca + filtros (pílulas) + tabela */}
            <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
              {/* Top: search + filter pills */}
              <div className="p-4 border-b flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                {/* Busca */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome ou código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-10"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Filtros como pílulas (substituiu o Select) */}
                <div className="flex gap-1.5 bg-muted/40 rounded-lg p-1 shrink-0">
                  {[
                    { key: 'all',    label: 'Todos',  count: ingredients.length, dot: null },
                    { key: 'low',    label: 'Baixo',  count: stockCounts.low,    dot: 'bg-red-500' },
                    { key: 'normal', label: 'Normal', count: stockCounts.normal, dot: 'bg-green-500' },
                    { key: 'high',   label: 'Alto',   count: stockCounts.high,   dot: 'bg-blue-500' },
                  ].map(opt => {
                    const active = filterStock === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setFilterStock(opt.key)}
                        className={`flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium transition-all ${
                          active
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {opt.dot && <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} />}
                        <span>{opt.label}</span>
                        {!primeiraCarga && (
                          <span className={`text-[10px] tabular-nums px-1.5 rounded-full ${active ? 'bg-muted text-muted-foreground' : 'bg-muted/60 text-muted-foreground'}`}>
                            {opt.count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Lista em LINHAS de duas alturas (09/09/2026): o dono preferiu
                  linhas a cards ("mais facil de ir acompanhando visualmente"),
                  mantendo a barra de estoque, a unidade em palavras e o custo
                  da ficha em um numero com a conta embaixo. */}
              {primeiraCarga ? (
                <CarregandoTabela linhas={8} colunas={5} className="" />
              ) : (
              <TabelaItensEstoque
                itens={paginatedIngredients}
                totalCadastrados={ingredients.length}
                totalFiltrados={filteredIngredients.length}
                getStatus={getStockStatus}
                ehReceita={(ing) => !!(ing.is_recipe || recipeIngredientIds.has(ing.id))}
                canEdit={canEdit}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onHistorico={openPriceAnalysis}
                onMovimentar={openStockDialog}
                onEditar={(ing) => ((ing.is_recipe || recipeIngredientIds.has(ing.id)) ? handleEditRecipe(ing) : handleEdit(ing))}
                onExcluir={(ing) => checkUsageAndDelete(ing.id, ing.name)}
              />
              )}

              {/* Paginação */}
              {filteredIngredients.length > 0 && (
                <TablePagination
                  currentPage={currentPage}
                  totalItems={filteredIngredients.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              )}
              
              {/* Nota sobre o preço médio */}
              <div className="px-4 py-3 border-t bg-muted/30 text-xs text-muted-foreground">
                * O Preço Médio é calculado com base na <strong>média das últimas 5 compras</strong> registradas para cada item.
              </div>
            </div>
          </div>
          )}

          {/* Arquivados: item com compras registradas nao pode ser excluido (quebraria
              os resultados) — fica aqui, fora das listas, e pode ser reativado. */}
          {activeTab === "arquivados" && (
            <div className="bg-card rounded-xl border shadow-sm overflow-hidden" data-e2e="painel-arquivados">
              <div className="px-4 py-3 border-b bg-muted/20 text-sm text-muted-foreground">
                Itens arquivados ficam fora do estoque, das fichas novas e das compras, mas o histórico deles continua nos relatórios. Reative quando precisar.
              </div>
              {primeiraCarga ? <CarregandoTabela linhas={4} colunas={5} className="" /> : (
              <TabelaItensEstoque
                arquivados
                itens={ingredients.filter((i) => i.is_active === false)}
                totalCadastrados={ingredients.length}
                totalFiltrados={ingredients.filter((i) => i.is_active === false).length}
                getStatus={getStockStatus}
                ehReceita={(ing) => !!(ing.is_recipe || recipeIngredientIds.has(ing.id))}
                canEdit={canEdit}
                sortField={null}
                sortDirection="asc"
                onSort={() => {}}
                onHistorico={openPriceAnalysis}
                onMovimentar={() => {}}
                onEditar={() => {}}
                onReativar={reactivateIngredient}
                onExcluir={(ing) => checkUsageAndDelete(ing.id, ing.name)}
              />
              )}
            </div>
          )}

          {/* Produção de receitas: baixa os ingredientes base, sobe o item pronto */}
          {activeTab === "producao" && (
            <ProducaoTab
              canEdit={canEdit}
              toast={toast}
              atualizacao={ingredients}
              onNovaReceita={openNewRecipe}
              onEditarReceita={(iid, pid) => { const ing = iid && ingredients.find((i) => i.id === iid); if (ing) handleEditRecipe(ing); else if (pid) editarReceitaPorProduto(pid); }}
            />
          )}

          {/* Movimentações */}
          {activeTab === "movimentacoes" && (
            <div>
              {/* Filtros de movimentações */}
              <div className="bg-card rounded-xl border shadow-sm p-4 mb-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por item, motivo ou referência..."
                      value={movementSearch}
                      onChange={(e) => setMovementSearch(e.target.value)}
                      className="pl-9 h-10"
                    />
                    {movementSearch && (
                      <button onClick={() => setMovementSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="w-full lg:w-56">
                    <Select value={movementFilter} onValueChange={setMovementFilter}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Tipo de movimentação" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as movimentações</SelectItem>
                        <SelectItem value="purchase">Compras (entrada)</SelectItem>
                        <SelectItem value="order_deduction">Vendas (saída)</SelectItem>
                        <SelectItem value="order_restore">Estornos (devolução)</SelectItem>
                        <SelectItem value="adjustment_in">Ajuste manual (entrada)</SelectItem>
                        <SelectItem value="adjustment_out">Ajuste manual (saída)</SelectItem>
                        <SelectItem value="production_in">Produção (entrada do item pronto)</SelectItem>
                        <SelectItem value="production_out">Produção (saída dos ingredientes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" className="h-10" onClick={fetchMovements} disabled={movementsLoading}>
                    <ArrowRightLeft className="w-4 h-4 mr-1.5" />
                    {movementsLoading && movimentosCarregados ? "Atualizando..." : "Atualizar"}
                  </Button>
                </div>
              </div>

              {/* Tabela de movimentações */}
              <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                {!movimentosCarregados ? (
                  <CarregandoTabela linhas={8} colunas={7} className="" />
                ) : (() => {
                  const filtered = movements.filter(m => {
                    if (movementFilter !== "all" && m.movement_type !== movementFilter) return false;
                    if (movementSearch) {
                      const s = movementSearch.toLowerCase();
                      return (m.ingredient_name || "").toLowerCase().includes(s) ||
                             (m.reason || "").toLowerCase().includes(s) ||
                             (m.reference_label || "").toLowerCase().includes(s) ||
                             (m.username || "").toLowerCase().includes(s);
                    }
                    return true;
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="p-12 text-center">
                        <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                        <p className="text-muted-foreground font-medium">Nenhuma movimentação encontrada</p>
                        <p className="text-sm text-muted-foreground mt-1">As movimentações aparecerão aqui quando houver compras, vendas ou ajustes de estoque.</p>
                      </div>
                    );
                  }

                  const typeConfig = {
                    purchase: { label: "Compra", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400", icon: "+" },
                    order_deduction: { label: "Venda", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: "-" },
                    order_restore: { label: "Estorno", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: "+" },
                    adjustment_in: { label: "Entrada Manual", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", icon: "+" },
                    adjustment_out: { label: "Saída Manual", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400", icon: "-" },
                    production_in: { label: "Produção (entrada)", color: "bg-primary/10 text-primary", icon: "+" },
                    production_out: { label: "Produção (saída)", color: "bg-primary/10 text-primary", icon: "-" },
                    production_undo_in: { label: "Estorno de produção", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: "+" },
                    production_undo_out: { label: "Estorno de produção", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: "-" },
                  };

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Data/Hora</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tipo</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Item</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Quantidade</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Estoque</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Motivo / Referência</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Usuário</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((mov) => {
                            const tc = typeConfig[mov.movement_type] || { label: mov.movement_type, color: "bg-gray-100 text-gray-700", icon: "" };
                            const isPositive = mov.quantity_change > 0;
                            const dateStr = mov.created_at ? new Date(mov.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-";
                            const isClickable = mov.reference_type === "order" || mov.reference_type === "purchase";

                            return (
                              <tr key={mov.id} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                                <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">
                                  <Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 opacity-50" />
                                  {dateStr}
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tc.color}`}>
                                    {tc.label}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-sm font-medium">
                                  {mov.ingredient_name || "-"}
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <span className={`font-mono font-semibold text-sm ${isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                                    {isPositive ? "+" : ""}{fmtNum(mov.quantity_change)}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right text-sm text-muted-foreground font-mono">
                                  {fmtNum(mov.quantity_before, 1)} → {fmtNum(mov.quantity_after, 1)}
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  {isClickable ? (
                                    <button
                                      onClick={() => {
                                        // O painel roda em /#/admin/...: o antigo
                                        // location.href="/vendas?pedido=" saía do painel.
                                        if (mov.reference_type === "order") {
                                          navigate(`/admin/vendas/pedidos?openPedido=${encodeURIComponent(mov.reference_id)}`);
                                        } else if (mov.reference_type === "purchase") {
                                          navigate("/admin/compras");
                                        }
                                      }}
                                      className="text-primary hover:underline flex items-center gap-1"
                                    >
                                      {mov.reference_label || mov.reason || "-"}
                                      <ExternalLink className="w-3 h-3" />
                                    </button>
                                  ) : (
                                    <span className="text-muted-foreground">{mov.reason || mov.reference_label || "-"}</span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-sm text-muted-foreground">
                                  <User className="w-3.5 h-3.5 inline mr-1 -mt-0.5 opacity-50" />
                                  {mov.username || "-"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Stock Adjustment Dialog */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajustar Estoque</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {stockIngredient?.name} - Estoque atual: {fmtNum(stockIngredient?.stock_quantity)} {stockIngredient?.unit}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                variant={stockOperation === "add" ? "default" : "outline"}
                onClick={() => setStockOperation("add")}
                className="h-12"
              >
                <ArrowUp className="w-5 h-5 mr-2" />
                Entrada
              </Button>
              <Button
                type="button"
                variant={stockOperation === "remove" ? "default" : "outline"}
                onClick={() => setStockOperation("remove")}
                className="h-12"
              >
                <ArrowDown className="w-5 h-5 mr-2" />
                Saída
              </Button>
            </div>
            
            <div>
              <Label>Quantidade</Label>
              <Input
                type="number"
                step="0.01"
                value={stockAdjustment}
                onChange={(e) => setStockAdjustment(e.target.value)}
                placeholder="0.00"
                className="mt-1 h-11"
              />
            </div>
            
            <div>
              <Label>Motivo (opcional)</Label>
              <Input
                value={stockReason}
                onChange={(e) => setStockReason(e.target.value)}
                placeholder="Ex: Perda, Ajuste de inventário, etc"
                className="mt-1 h-11"
              />
            </div>
            
            <Button
              onClick={handleStockAdjustment}
              disabled={!stockAdjustment || parseFloat(stockAdjustment) <= 0}
              className="w-full h-11"
            >
              Confirmar Ajuste
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicar Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Criando cópia de: <strong>{ingredientToDuplicate?.name}</strong>
            </p>
            <div>
              <Label htmlFor="duplicateName">Nome do novo item</Label>
              <Input
                id="duplicateName"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Digite o nome do novo item"
                className="mt-1 h-11"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              A cópia terá as mesmas configurações (unidade, limites de estoque), 
              mas começará com estoque zerado e preço médio zerado.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDuplicateDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDuplicateIngredient}
                disabled={loading || !duplicateName.trim()}
                className="flex-1"
              >
                {loading ? "Duplicando..." : "Duplicar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog - Ingredient in use */}
      <AlertDialog open={deleteWarningOpen} onOpenChange={setDeleteWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Não é possível excluir este item</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                O item <strong>{ingredientToDelete}</strong> está sendo usado nos seguintes produtos:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {productsUsingIngredient.map((product) => (
                  <li key={product.id}>{product.name}</li>
                ))}
              </ul>
              <p className="text-sm pt-2">
                Para excluir este item, primeiro remova-o das fichas técnicas dos produtos acima.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Entendi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Receita — nível da página: usado pela aba Estoque e pela aba Produção */}
      {canEdit && (
        <Dialog open={recipeOpen} onOpenChange={(isOpen) => {
          setRecipeOpen(isOpen);
          if (!isOpen) resetRecipeForm();
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-orange-500" />
                {recipeEditMode ? "Editar Receita" : "Nova Receita"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submitRecipe} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="recipe-name">Nome da Receita</Label>
                <Input
                  id="recipe-name"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  placeholder="Ex: Molho especial, Carne marinada"
                  required
                  className="mt-1 h-11"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="recipe-yield-qty">Rendimento</Label>
                  <Input
                    id="recipe-yield-qty"
                    type="number"
                    step="0.001"
                    value={recipeYieldQty}
                    onChange={(e) => setRecipeYieldQty(e.target.value)}
                    placeholder="Ex: 0.500"
                    required
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="recipe-yield-unit">Unidade</Label>
                  <select
                    id="recipe-yield-unit"
                    value={recipeYieldUnit}
                    onChange={(e) => setRecipeYieldUnit(e.target.value)}
                    className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="kg">kg</option>
                    <option value="un">un</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="recipe-loss">Perda (%)</Label>
                  <Input
                    id="recipe-loss"
                    type="number"
                    step="0.01"
                    min="0"
                    max="99"
                    value={recipeLossPercent}
                    onChange={(e) => setRecipeLossPercent(e.target.value)}
                    placeholder="0"
                    className="mt-1 h-11"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-2">
                Perda eh aplicada no preço unitário gerado (ex.: receita rende 500g mas só 450g sao usaveis = perda 10% → custo do kg sobe 11%).
              </p>

              {/* Ingredientes da Receita — mesmo padrao da Ficha Tecnica em Produtos */}
              <div className="bg-card rounded-2xl border shadow-sm flex flex-col">
                <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50/80 to-green-50/80 dark:from-emerald-950/30 dark:to-green-950/30 flex items-center justify-between rounded-t-2xl">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                      <span className="text-base">🥬</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Ingredientes</h3>
                      <p className="text-[10px] text-muted-foreground">{recipeItems.filter(it => it.ingredient_id).length} item(ns)</p>
                    </div>
                  </div>
                  {recipeItems.filter(it => it.ingredient_id).length > 0 && (
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Subtotal</p>
                      <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">R$ {recipeTotalCost.toFixed(2).replace(".", ",")}</p>
                    </div>
                  )}
                </div>
                <div className="p-3 flex-1">
                  {/* Busca direta — digitar mostra opções, clicar adiciona com qtd vazia */}
                  <div className="mb-3">
                    <RecipeIngredientSearch
                      ingredients={ingredients}
                      excludeIds={[
                        ...recipeItems.map(r => r.ingredient_id).filter(Boolean),
                        // exclui o proprio ingrediente vinculado (no modo edicao) pra evitar autoreferencia
                        recipeIngredientId,
                      ]}
                      onAdd={(id) => setRecipeItems(prev => [...prev, { ingredient_id: id, quantity: "" }])}
                    />
                  </div>

                  {recipeItems.filter(it => it.ingredient_id).length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-3">
                      Busque acima pra adicionar ingredientes (inclusive outras receitas).
                    </p>
                  )}

                  {recipeItems.filter(it => it.ingredient_id).length > 0 && (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          {recipeItems.filter(it => it.ingredient_id).length} adicionado{recipeItems.filter(it => it.ingredient_id).length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex-1 h-px bg-border" />
                      </div>

                      <div className="space-y-2">
                        {recipeItems.map((it, idx) => {
                          if (!it.ingredient_id) return null;
                          const ing = ingredients.find(i => i.id === it.ingredient_id);
                          const cost = calcRecipeItemCost(ing, it.quantity);
                          const uw = Number(ing?.unit_weight) || 0;
                          const fractionText = uw > 0
                            ? `1 un = ${uw.toFixed(3).replace('.', ',')} kg`
                            : `1 ${ing?.unit || "un"} = 1 ${ing?.unit || "un"}`;
                          // Quando a quantidade da receita esta sendo consumida em kg
                          // (ing.unit=kg sem uw, OU ing.unit=un com uw>0) e o valor for
                          // fracionario (< 1kg), mostra a equivalencia em gramas pro
                          // lojista visualizar gramatura ('800 g' em vez de '0,8 kg').
                          const _q = parseFloat(it.quantity) || 0;
                          const consumedInKg = (ing?.unit === "kg" && uw === 0) || (ing?.unit === "un" && uw > 0);
                          const showGrams = consumedInKg && _q > 0 && _q < 1;
                          const qtyInGrams = showGrams ? Math.round(_q * 1000) : null;
                          return (
                            <div key={idx} className={`flex items-center gap-2 p-2.5 rounded-xl border transition-colors ${
                              ing?.is_recipe
                                ? 'bg-orange-50/40 dark:bg-orange-950/20 border-orange-300/40 hover:bg-orange-50/70 dark:hover:bg-orange-950/30'
                                : 'bg-muted/20 hover:bg-muted/40'
                            }`}>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate flex items-center gap-1.5">
                                  {ing?.is_recipe && <ChefHat className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                                  {ing?.name || "—"}
                                </p>
                                <p className="text-[10px] text-muted-foreground tabular-nums">{fractionText}</p>
                              </div>
                              <div className="w-24 flex-shrink-0 flex flex-col items-stretch gap-0.5">
                                <Input
                                  type="number"
                                  step="0.001"
                                  value={it.quantity}
                                  onChange={(e) => updateRecipeItem(idx, "quantity", e.target.value)}
                                  placeholder="Qtd"
                                  className="h-8 text-sm text-center"
                                />
                                {qtyInGrams != null ? (
                                  <span className="text-center text-[10px] font-bold text-amber-600 dark:text-amber-400 tabular-nums leading-none">
                                    = {qtyInGrams} g
                                  </span>
                                ) : (
                                  <span className="text-center text-[10px] text-transparent select-none leading-none">·</span>
                                )}
                              </div>
                              <div className={`text-xs font-semibold px-2 py-1.5 rounded-lg whitespace-nowrap ${
                                cost > 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground bg-muted/30'
                              }`}>
                                {cost > 0 ? `R$ ${cost.toFixed(2).replace(".", ",")}` : "R$ —"}
                              </div>
                              <Button type="button" onClick={() => removeRecipeItem(idx)} variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg flex-shrink-0">
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Custo total da receita + custo por unidade do rendimento */}
              <div className="rounded-2xl border-2 border-orange-300/50 dark:border-orange-700/40 bg-gradient-to-br from-orange-50/70 to-amber-50/70 dark:from-orange-950/30 dark:to-amber-950/30 p-4 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Custo total da receita</span>
                  <span className="font-mono font-semibold tabular-nums">R$ {recipeTotalCost.toFixed(2).replace(".", ",")}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-orange-300/30 dark:border-orange-700/30">
                  <div>
                    <p className="text-[10px] text-orange-700 dark:text-orange-300 uppercase tracking-wider font-bold">Preço unitário gerado</p>
                    <p className="text-[11px] text-muted-foreground">vira preço médio de '{recipeName || "(receita)"}'</p>
                  </div>
                  <span className="font-mono font-bold text-xl tabular-nums text-orange-600 dark:text-orange-400">
                    R$ {recipeUnitCost.toFixed(2).replace(".", ",")}<span className="text-sm font-medium text-muted-foreground">/{recipeYieldUnit}</span>
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={recipeSaving} className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground">
                  {recipeSaving ? (recipeEditMode ? "Atualizando…" : "Criando…") : (recipeEditMode ? "Atualizar Receita" : "Criar Receita")}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setRecipeOpen(false); resetRecipeForm(); }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Arquivar: item com compras registradas */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => { if (!o) setArchiveTarget(null); }}>
        <AlertDialogContent data-e2e="dialog-arquivar">
          <AlertDialogHeader>
            <AlertDialogTitle>Este item tem compras registradas</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>{archiveTarget?.name}</strong> tem {archiveTarget?.compras} compra{archiveTarget?.compras === 1 ? "" : "s"} registrada{archiveTarget?.compras === 1 ? "" : "s"}.
                Excluir apagaria esse histórico e mudaria os resultados dos relatórios.
              </p>
              <p>Em vez disso ele vai para a aba <strong>Arquivados</strong>: sai do estoque, das fichas novas e das compras, e pode ser reativado quando quiser.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmArchive} data-e2e="confirmar-arquivar">Arquivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-e2e="dialog-excluir">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{ingredientToDelete?.name}</strong> não tem compras registradas e será excluído de vez.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-e2e="confirmar-excluir"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === Modal: Histórico de Preços + Análise === */}
      <Dialog open={priceAnalysisOpen} onOpenChange={setPriceAnalysisOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <LineChartIcon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-base font-bold">Histórico de Preços</p>
                <p className="text-xs text-muted-foreground font-normal">{priceAnalysisData?.ingredient?.name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {priceAnalysisLoading ? (
            <div className="space-y-4">
              <CarregandoCards quantos={4} className="grid grid-cols-2 md:grid-cols-4 gap-2" altura="h-[92px]" />
              <CarregandoBloco altura="h-56" />
            </div>
          ) : !priceAnalysisData || !priceAnalysisData.history || priceAnalysisData.history.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              {priceAnalysisData?.ingredient?.is_recipe ? (
                <>
                  <ChefHat className="w-12 h-12 mx-auto opacity-30 mb-3" />
                  <p className="font-medium">Sem histórico de custo da receita</p>
                  <p className="text-xs mt-1">A cada vez que algum ingrediente desta receita mudar de preço, o custo recalculado fica registrado aqui (últimos 5).</p>
                </>
              ) : (
                <>
                  <Package className="w-12 h-12 mx-auto opacity-30 mb-3" />
                  <p className="font-medium">Sem histórico de compras</p>
                  <p className="text-xs mt-1">Lance compras pra começar a ver evolução de preço.</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* KPIs principais — 3 cards (Preço médio dual, Compras, Total gasto + qty, Variação com seta) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {(() => {
                  const s = priceAnalysisData.stats || {};
                  const unit = priceAnalysisData.ingredient?.unit || 'un';
                  const dir = s.direction || 'flat';
                  const variationColor = dir === 'up' ? 'text-red-500' : dir === 'down' ? 'text-emerald-500' : 'text-foreground';
                  const VariationIcon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : ArrowUpDown;
                  const fmtQty = (q) => {
                    if (!q || q <= 0) return null;
                    const n = Number(q);
                    return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2).replace('.', ',');
                  };
                  const qtyStr = fmtQty(s.total_qty);
                  return (
                    <>
                      {/* Preço médio: Últimas 5 (usado pelo sistema) + Geral */}
                      <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Preço médio</p>
                        <p className="text-base font-bold tabular-nums leading-tight">
                          R$ {(s.avg_price_last_5 || 0).toFixed(2).replace('.', ',')}
                          <span className="text-[10px] font-normal text-muted-foreground">/{unit}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground -mt-0.5">últimas 5 · usado no CMV</p>
                        <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                          Geral: R$ {(s.avg_price || 0).toFixed(2).replace('.', ',')}/{unit}
                        </p>
                      </div>
                      {/* Compras */}
                      <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Compras</p>
                        <p className="text-base font-bold tabular-nums leading-tight">{s.total_purchases || 0}</p>
                        <p className="text-[10px] text-muted-foreground">no histórico</p>
                      </div>
                      {/* Total gasto + Qtd comprada */}
                      <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total gasto</p>
                        <p className="text-base font-bold tabular-nums leading-tight">R$ {(s.total_spent || 0).toFixed(2).replace('.', ',')}</p>
                        {qtyStr && (
                          <p className="text-[10px] text-muted-foreground tabular-nums">{qtyStr} {unit} comprado{qtyStr === '1' ? '' : 's'}</p>
                        )}
                      </div>
                      {/* Variação com seta direcional */}
                      <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Variação</p>
                        <p className={`text-base font-bold tabular-nums leading-tight inline-flex items-center gap-1 ${variationColor}`}>
                          <VariationIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {(s.variation_pct || 0).toFixed(1).replace('.', ',')}%
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {dir === 'up' ? 'subindo' : dir === 'down' ? 'caindo' : 'estável'}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Cards combinados: Compra mais barata + Melhor fornecedor | Compra mais cara + Pior fornecedor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* === BARATO + MELHOR === */}
                {priceAnalysisData.cheapest && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingDown className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Compra mais barata</span>
                    </div>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400 leading-tight">
                      R$ {priceAnalysisData.cheapest.price.toFixed(2).replace('.', ',')}
                    </p>
                    <div className="text-[11px] text-muted-foreground mt-1.5 space-y-0.5">
                      <div className="flex items-center gap-1.5"><Truck className="w-3 h-3" /> {priceAnalysisData.cheapest.supplier || '—'}</div>
                      <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {new Date(priceAnalysisData.cheapest.date).toLocaleDateString('pt-BR')}</div>
                    </div>
                    {/* Melhor fornecedor — divisor sutil dentro do mesmo card */}
                    {priceAnalysisData.best_supplier && (
                      <div className="mt-3 pt-2.5 border-t border-emerald-500/20">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Award className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Melhor fornecedor</span>
                        </div>
                        <p className="font-semibold truncate text-sm">{priceAnalysisData.best_supplier.supplier}</p>
                        <p className="text-[11px] text-muted-foreground tabular-nums">
                          Média R$ {priceAnalysisData.best_supplier.avg_price.toFixed(2).replace('.', ',')} · {priceAnalysisData.best_supplier.purchases} compra{priceAnalysisData.best_supplier.purchases !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* === CARO + PIOR === */}
                {priceAnalysisData.most_expensive && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingUp className="w-4 h-4 text-red-600" />
                      <span className="text-xs font-semibold text-red-700 dark:text-red-400">Compra mais cara</span>
                    </div>
                    <p className="text-2xl font-bold tabular-nums text-red-600 dark:text-red-400 leading-tight">
                      R$ {priceAnalysisData.most_expensive.price.toFixed(2).replace('.', ',')}
                    </p>
                    <div className="text-[11px] text-muted-foreground mt-1.5 space-y-0.5">
                      <div className="flex items-center gap-1.5"><Truck className="w-3 h-3" /> {priceAnalysisData.most_expensive.supplier || '—'}</div>
                      <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {new Date(priceAnalysisData.most_expensive.date).toLocaleDateString('pt-BR')}</div>
                    </div>
                    {/* Pior fornecedor — só quando há mais de 1 fornecedor distinto */}
                    {priceAnalysisData.worst_supplier && priceAnalysisData.best_supplier &&
                     priceAnalysisData.worst_supplier.supplier !== priceAnalysisData.best_supplier.supplier && (
                      <div className="mt-3 pt-2.5 border-t border-red-500/20">
                        <div className="flex items-center gap-1.5 mb-1">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">Pior fornecedor</span>
                        </div>
                        <p className="font-semibold truncate text-sm">{priceAnalysisData.worst_supplier.supplier}</p>
                        <p className="text-[11px] text-muted-foreground tabular-nums">
                          Média R$ {priceAnalysisData.worst_supplier.avg_price.toFixed(2).replace('.', ',')} · {priceAnalysisData.worst_supplier.purchases} compra{priceAnalysisData.worst_supplier.purchases !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Gráfico de evolução */}
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-4 py-2.5 border-b bg-muted/20 flex items-center gap-2 flex-wrap">
                  <LineChartIcon className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">Evolução do preço</h3>
                  {/* Filtro de período: Todo período | Este Mês | Personalizado */}
                  <div className="ml-auto flex items-center gap-1 flex-wrap">
                    {[
                      { key: 'all',    label: 'Todo período' },
                      { key: 'month',  label: 'Este mês' },
                      { key: 'custom', label: 'Personalizado' },
                    ].map(opt => {
                      const active = priceDateFilter === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setPriceDateFilter(opt.key)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                            active
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {priceDateFilter === 'custom' && (
                    <div className="w-full flex items-center gap-2 mt-1">
                      <Input
                        type="date"
                        value={priceCustomStart}
                        onChange={(e) => setPriceCustomStart(e.target.value)}
                        className="h-8 text-xs"
                      />
                      <span className="text-xs text-muted-foreground">até</span>
                      <Input
                        type="date"
                        value={priceCustomEnd}
                        onChange={(e) => setPriceCustomEnd(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  {filteredPriceHistory.length === 0 ? (
                    <div className="h-[260px] flex flex-col items-center justify-center text-muted-foreground">
                      <LineChartIcon className="w-8 h-8 mb-2 opacity-30" />
                      <p className="text-sm">Sem compras no período selecionado</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={filteredPriceHistory} margin={{ top: 24, right: 16, bottom: 8, left: 0 }}>
                        <defs>
                          <linearGradient id="ingPriceGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10 }}
                          className="fill-muted-foreground"
                          tickFormatter={d => {
                            const p = String(d).split('T')[0].split('-');
                            return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(-2)}` : d;
                          }}
                        />
                        <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" tickFormatter={v => `R$${String(v).replace('.', ',')}`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 12 }}
                          formatter={(v, _name, item) => [`R$ ${Number(v).toFixed(2).replace('.', ',')} · ${item?.payload?.supplier || '—'}`, "Preço"]}
                          labelFormatter={d => {
                            const p = String(d).split('T')[0].split('-');
                            return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="price"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          fill="url(#ingPriceGrad)"
                          dot={{ fill: "hsl(var(--primary))", r: 4, strokeWidth: 2, stroke: "hsl(var(--background))" }}
                          activeDot={{ r: 6, fill: "hsl(var(--primary))", stroke: "#fff", strokeWidth: 2 }}
                          label={{
                            position: 'top',
                            fontSize: 10,
                            fill: 'hsl(var(--foreground))',
                            formatter: (v) => `R$${Number(v).toFixed(2).replace('.', ',')}`,
                          }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {/* Tabela de fornecedores (sempre, se houver) */}
              {priceAnalysisData.supplier_breakdown && priceAnalysisData.supplier_breakdown.length > 0 && (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="px-4 py-2.5 border-b bg-muted/20 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">Por fornecedor</h3>
                    <span className="text-[10px] text-muted-foreground ml-auto">ordenado por preço médio</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/10">
                        <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          <th className="text-left py-2 px-3">Fornecedor</th>
                          <th className="text-right py-2 px-3">Preço médio</th>
                          <th className="text-right py-2 px-3">Compras</th>
                          <th className="text-right py-2 px-3">Total gasto</th>
                          <th className="text-right py-2 px-3">Última</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priceAnalysisData.supplier_breakdown.map((s, i, arr) => {
                          const isBest = i === 0 && arr.length > 1;
                          const isWorst = i === arr.length - 1 && arr.length > 1;
                          return (
                          <tr key={i} className={`border-t ${isBest ? 'bg-emerald-500/5' : isWorst ? 'bg-red-500/5' : ''}`}>
                            <td className="py-2 px-3 font-medium">
                              {s.supplier}
                              {isBest && <span className="ml-1.5 text-[9px] text-emerald-600 font-bold">★ MELHOR</span>}
                              {isWorst && <span className="ml-1.5 text-[9px] text-red-600 font-bold">▼ PIOR</span>}
                            </td>
                            <td className="py-2 px-3 text-right font-mono tabular-nums">R$ {s.avg_price.toFixed(2).replace('.', ',')}</td>
                            <td className="py-2 px-3 text-right tabular-nums">{s.purchases}</td>
                            <td className="py-2 px-3 text-right font-mono tabular-nums text-muted-foreground">R$ {s.total_spent.toFixed(2).replace('.', ',')}</td>
                            <td className="py-2 px-3 text-right text-muted-foreground tabular-nums">{new Date(s.last_date).toLocaleDateString('pt-BR')}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
